import React, {
  ChangeEvent,
  forwardRef,
  SyntheticEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import DeleteIcon from '@mui/icons-material/Delete'
import DoneIcon from '@mui/icons-material/Done'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Radio,
  Stack,
  TextField,
} from '@mui/material'

import { isScreenSizeSmall } from '../common/utils'

import { EmptyState } from './EmptyState'

type InsertSelectListProps = {
  options: ListOption[]
  selectedIndex: number | undefined
  inputPlaceHolder?: string
  onSelect: (selectedOption: ListOption | undefined) => void
  onDelete: (deletedOption: ListOption) => void
  onTextChange: (updatedOption: ListOption) => void
  onItemAdded: (newOption: ListOption, index: number) => void
  validateFn: (newValue: string, reset?: boolean) => boolean
  disabled: boolean
}

export type ListOption = {
  value: string
  text: string
}

type EditListOption = ListOption & {
  index: number
}

export type InsertSelectListPropsRef = InsertSelectListProps & {
  reset: () => void
}
export const InsertSelectList = forwardRef(
  (
    {
      options,
      selectedIndex,
      inputPlaceHolder,
      onSelect,
      onDelete,
      onTextChange,
      onItemAdded,
      validateFn,
      disabled,
    }: InsertSelectListProps,
    ref,
  ) => {
    const [activeEditListItem, setActiveEditListItem] =
      useState<EditListOption>()
    const { t } = useTranslation()
    const [listOptions, setListOptions] = useState<ListOption[]>([])
    const [inputValue, setInputValue] = useState('')
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)
    const [deletedOption, setDeletedOption] = useState<ListOption | undefined>(
      undefined,
    )

    const defaultSelected = useMemo(() => {
      if (selectedIndex !== undefined && options.length > 0) {
        return { ...options[selectedIndex] }
      }
      return undefined
    }, [selectedIndex, options])

    const [selectedOption, setSelectedOption] = useState<
      ListOption | undefined
    >(undefined)

    useEffect(() => {
      if (defaultSelected) {
        setSelectedOption(defaultSelected)
        onSelect(defaultSelected)
      } else {
        setSelectedOption(undefined)
        onSelect(undefined)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultSelected])

    useEffect(() => {
      setListOptions(options)
    }, [options])

    useImperativeHandle(ref, () => ({
      reset: () => {
        resetInsertSelectList()
      },
    }))

    const resetInsertSelectList = useCallback(() => {
      setSelectedOption(defaultSelected || undefined)
      if (defaultSelected) {
        onSelect(defaultSelected)
      }
      setActiveEditListItem(undefined)
      setInputValue('')
      setDeletedOption(undefined)
    }, [defaultSelected, onSelect])

    const handleDelete = (deletedOption: ListOption) => {
      setDeletedOption(deletedOption)
      setOpenDeleteConfirmation(true)
    }

    const handleSelect = (selected: ListOption) => {
      setSelectedOption(selected)
      onSelect(selected)
    }

    const handleOptionTextChange = (newValue: string) => {
      if (activeEditListItem) {
        setActiveEditListItem({ ...activeEditListItem, text: newValue })
      }
    }

    const handleInputChange = (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ): void => {
      setInputValue(event.target.value)
    }

    const handleAddClick = (): void => {
      if (!validateFn(inputValue)) {
        return
      }
      const newIndex = listOptions.length
      const newOption = { value: inputValue, text: inputValue }
      setListOptions([...listOptions, newOption])
      setSelectedOption(newOption)
      onItemAdded(newOption, newIndex)
      setInputValue('')
    }

    const handleInputSelected = (
      _event: SyntheticEvent<HTMLDivElement, Event>,
    ): void => {
      setActiveEditListItem(undefined)
    }

    const handleEditClick = (option: ListOption, index: number): void => {
      setActiveEditListItem({ ...option, index })
      handleSelect(option)
    }

    const handleCancelEdit = (index: number): void => {
      setActiveEditListItem(undefined)
      const cancelEditOption = listOptions.find((_item, idx) => idx === index)
      if (cancelEditOption) {
        validateFn(cancelEditOption.text, true)
      }
    }

    function handleSaveItem(index: number) {
      if (activeEditListItem) {
        if (!validateFn(activeEditListItem.text)) {
          return
        }

        const updatedOptions = listOptions.map((item, idx) =>
          idx === index ? { ...item, text: activeEditListItem.text } : item,
        )
        setListOptions(updatedOptions)
        setActiveEditListItem(undefined)

        let updatedOptionOption = listOptions.find(
          (_item, idx) => idx === index,
        )
        if (updatedOptionOption) {
          updatedOptionOption = {
            ...updatedOptionOption,
            text: activeEditListItem.text,
          }
          onTextChange(updatedOptionOption)
        }
      }
    }

    const handleDeleteClose = (): void => {
      setOpenDeleteConfirmation(false)
      setDeletedOption(undefined)
    }

    const handleDeleteConfirmation = (): void => {
      setOpenDeleteConfirmation(false)
      if (deletedOption) {
        setListOptions(
          listOptions.filter((option) =>
            option.value
              ? option.value !== deletedOption.value
              : option.text !== deletedOption.text,
          ),
        )
        setDeletedOption(undefined)
        onDelete(deletedOption)
      }
    }

    const renderOptionsList = (): React.JSX.Element => {
      return (
        <List sx={{ padding: 0, margin: 0 }}>
          {listOptions.map((option, index) => renderListItem(option, index))}
        </List>
      )
    }

    const renderListItem = (
      option: ListOption,
      index: number,
    ): React.JSX.Element => {
      return (
        <ListItem
          key={option.value}
          disableGutters
          dense
          sx={{
            padding: 0,
            margin: 0,
            height:
              selectedOption?.value === option.value
                ? 'auto'
                : getResponsiveHeight(),
          }}
        >
          <FormControlLabel
            control={
              <Radio
                checked={selectedOption?.value === option.value}
                onChange={() => handleSelect(option)}
                value={option.value}
                sx={responsiveIconStyle}
              />
            }
            label={
              activeEditListItem && activeEditListItem.index === index ? (
                <TextField
                  value={activeEditListItem.text}
                  onChange={(e) => handleOptionTextChange(e.target.value)}
                  variant="outlined"
                  size={isScreenSizeSmall() ? 'medium' : 'small'}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: isScreenSizeSmall()
                        ? 'var(--small-screen-font-size)'
                        : 'inherit',
                    },
                  }}
                />
              ) : (
                <ListItemText
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: isScreenSizeSmall()
                        ? 'var(--small-screen-font-size)'
                        : 'inherit',
                    },
                    maxWidth: '25vw',
                    overflowX: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace:
                      selectedOption?.value === option.value
                        ? 'normal'
                        : 'nowrap',
                    wordBreak:
                      selectedOption?.value === option.value
                        ? 'break-all'
                        : 'inherit',
                  }}
                  primary={option.text}
                  primaryTypographyProps={{
                    noWrap:
                      selectedOption?.value === option.value ? false : true,
                  }}
                />
              )
            }
            sx={{
              margin: 0,
              typography: 'body2',
              display: 'flex',
              alignItems: 'flex-start',
            }}
          />
          <ListItemSecondaryAction>
            {activeEditListItem && activeEditListItem.index === index
              ? renderActiveListItemActions(index)
              : renderListItemActions(option, index)}
          </ListItemSecondaryAction>
        </ListItem>
      )
    }

    const renderActiveListItemActions = (index: number): React.JSX.Element => {
      return (
        <>
          <IconButton
            edge="end"
            aria-label="Cancel"
            onClick={() => {
              handleCancelEdit(index)
            }}
            sx={responsiveIconStyle}
          >
            <ClearIcon />
          </IconButton>
          <IconButton
            edge="end"
            aria-label="Save"
            onClick={() => {
              handleSaveItem(index)
            }}
            sx={responsiveIconStyle}
          >
            <DoneIcon />
          </IconButton>
        </>
      )
    }

    const renderListItemActions = (
      option: ListOption,
      index: number,
    ): React.JSX.Element => {
      return (
        <>
          <IconButton
            edge="end"
            aria-label="edit"
            disabled={activeEditListItem && activeEditListItem.index !== index}
            onClick={() => {
              handleEditClick(option, index)
            }}
            sx={responsiveIconStyle}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            edge="end"
            aria-label="delete"
            disabled={activeEditListItem && activeEditListItem.index !== index}
            onClick={() => handleDelete(option)}
            sx={responsiveIconStyle}
          >
            <DeleteIcon fontSize={isScreenSizeSmall() ? 'large' : 'small'} />
          </IconButton>
        </>
      )
    }

    const responsiveFontStyle = {
      fontSize: isScreenSizeSmall()
        ? 'var(--small-screen-font-size)'
        : 'inherit',
    }

    const responsiveIconStyle = {
      '& .MuiSvgIcon-root': {
        fontSize: isScreenSizeSmall()
          ? 'var(--small-screen-font-size)'
          : 'small',
      },
    }

    const getResponsiveHeight = () => {
      if (isScreenSizeSmall()) {
        return 'calc(var(--small-screen-font-size) + 40px)'
      } else {
        return 'calc(var(--small-screen-font-size) + 15px)'
      }
    }

    return (
      <>
        <Grid
          container
          direction="column"
          spacing={2}
          padding={2}
          sx={{ height: '100%' }}
        >
          <Grid item sx={{ height: '20%' }}>
            <Box>
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-start"
                spacing={2}
              >
                <TextField
                  disabled={disabled}
                  variant="outlined"
                  fullWidth
                  value={inputValue}
                  placeholder={inputPlaceHolder}
                  onSelect={handleInputSelected}
                  onChange={handleInputChange}
                  sx={{
                    height: getResponsiveHeight(),
                    '& .MuiInputBase-root': {
                      height: '100%',
                      fontSize: isScreenSizeSmall()
                        ? 'var(--small-screen-font-size)'
                        : 'inherit',
                    },
                  }}
                />
                <Button
                  variant="contained"
                  color="inherit"
                  size="small"
                  onClick={handleAddClick}
                  sx={{
                    height: getResponsiveHeight(),
                    padding: '0 16px',
                  }}
                  disabled={!inputValue}
                >
                  <div className="responsive-icon">
                    <AddIcon
                      fontSize={isScreenSizeSmall() ? 'large' : 'small'}
                    />
                  </div>
                  <div className="responsive-font">{t('add')}</div>
                </Button>
              </Stack>
            </Box>
          </Grid>
          <Grid item sx={{ height: '78%' }}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                overflowY: 'auto',
                padding: 2,
                borderTop: 1,
                borderColor: 'grey.500',
              }}
            >
              <div style={{ height: '300px' }}>
                {listOptions.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '300px',
                    }}
                  >
                    <EmptyState />
                  </Box>
                ) : (
                  renderOptionsList()
                )}
              </div>
            </Paper>
          </Grid>
        </Grid>

        <Dialog
          open={openDeleteConfirmation}
          onClose={handleDeleteClose}
          aria-labelledby="delete-alert-dialog-title"
          aria-describedby="delete-alert-dialog-description"
        >
          <DialogTitle id="delete-alert-dialog-title" sx={responsiveFontStyle}>
            {t('delete_entraid')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              sx={responsiveFontStyle}
              id="delete-alert-dialog-description"
            >
              {t('confrim_entraid_delete')} {deletedOption?.text}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button sx={responsiveFontStyle} onClick={handleDeleteClose}>
              {t('cancel')}
            </Button>
            <Button
              sx={responsiveFontStyle}
              onClick={handleDeleteConfirmation}
              autoFocus
              variant="contained"
            >
              {t('ok')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  },
)

InsertSelectList.displayName = 'InsertSelectList'
